/**
 * ConstructionWalk English strings.
 * Keys are stable so an `es` locale can follow the same shape later.
 */
export const en = {
  nav: {
    dashboard: "Dashboard",
    companies: "Companies",
    jobCodes: "Job Codes",
    jobSites: "Job Sites",
    employees: "Employees",
    adminMode: "Admin mode",
    timeClock: "Time clock",
  },
  common: {
    add: "Add",
    edit: "Edit",
    delete: "Delete",
    cancel: "Cancel",
    save: "Save",
    saving: "Saving…",
    close: "Close",
    actions: "Actions",
    confirm: "Confirm",
    deleting: "Deleting…",
  },
  companies: {
    title: "Companies",
    subtitle: "Construction companies",
    add: "Add company",
    adding: "Adding…",
    cancel: "Cancel",
    changeCompany: "Change company",
    empty: "No companies yet.",
    columnCompany: "Company",
    columnCode: "Code",
    modalTitle: "Add company",
    modalHelper: "Company name and a short code. You can add job sites after.",
    nameLabel: "Company name",
    codeLabel: "Company code",
    codeHelper: "Shown on job sites and time. Letters and numbers.",
    nameRequired: "Enter a company name.",
    codeRequired: "Enter a company code.",
    codeDuplicate: "That code is already in use.",
    submitFailed: "Could not add the company. Try again.",
    loadFailed: "Could not load companies. Try again.",
  },
  jobCodes: {
    title: "Job Codes",
    empty: "No job codes yet.",
    loadFailed: "Could not load job codes. Try again.",
    columnCode: "Code",
    columnName: "Name",
    columnJobSite: "Job site",
    add: "Add job code",
    modalAddTitle: "Add job code",
    modalEditTitle: "Edit job code",
    codeLabel: "Job code",
    codeHelper: "Sage cost code. A number with up to 3 decimals, such as 2120 or 2120.5.",
    nameLabel: "Name",
    jobSiteLabel: "Job site",
    codeRequired: "Enter a job code.",
    codeInvalid: "Enter a number with up to 3 decimals, such as 2120 or 2120.5.",
    nameRequired: "Enter a name.",
    jobSiteRequired: "Choose a job site.",
    deleteTitle: "Delete job code",
    deleteConfirm: "Delete this job code? This cannot be undone.",
    drop: "Drop job codes here, or choose a file",
    dropHelper: "Sage workbook or a list of codes. They are added to this job site.",
    importing: "Adding job codes…",
    importAdded: "Added {count} job codes.",
    importFailedRows: "Could not add these rows:",
    importRowFailed: "Row {row}: {detail}",
    importInvalidFile: "Drop a Sage workbook or a list of job codes.",
    importEmpty: "That list did not have any job codes.",
    importFailed: "Could not add those job codes. Try again.",
  },
  jobSites: {
    title: "Job Sites",
    empty: "No job sites yet.",
    loadFailed: "Could not load job sites. Try again.",
    columnName: "Job site",
    columnCode: "Number",
    columnAddress: "Address",
    columnSupervisor: "Supervisor",
    add: "Add job site",
    modalAddTitle: "Add job site",
    modalEditTitle: "Edit job site",
    jobNumberLabel: "Job site number",
    jobNumberHelper: "Sage job number. A whole number greater than 0.",
    nameLabel: "Name",
    addressLabel: "Address",
    cityLabel: "City",
    stateLabel: "State",
    zipLabel: "ZIP",
    supervisorLabel: "Supervisor",
    jobNumberRequired: "Enter a job site number.",
    jobNumberInvalid: "Enter a whole number greater than 0.",
    nameRequired: "Enter a name.",
    deleteTitle: "Delete job site",
    deleteConfirm: "Delete this job site? This cannot be undone.",
    view: "View",
  },
  employees: {
    title: "Employees",
    empty: "No employees yet.",
    loadFailed: "Could not load employees. Try again.",
    columnName: "Name",
    columnNumber: "Number",
    columnRole: "Role",
    add: "Add employee",
    modalAddTitle: "Add employee",
    modalEditTitle: "Edit employee",
    employeeNumLabel: "Employee number",
    employeeNumHelper: "Sage employee number. A whole number greater than 0.",
    firstNameLabel: "First name",
    lastNameLabel: "Last name",
    employeeNumRequired: "Enter an employee number.",
    employeeNumInvalid: "Enter a whole number greater than 0.",
    firstNameRequired: "Enter a first name.",
    lastNameRequired: "Enter a last name.",
    deleteTitle: "Delete employee",
    deleteConfirm: "Delete this employee? This cannot be undone.",
  },
  company: {
    missing: "This page needs a selected company.",
  },
  import: {
    drop: "Drop a Sage workbook here, or choose a file",
    helper: "Excel workbook from Sage. Extra columns are ignored.",
    sourceEmployees: "From Sage PR 5-2-1 Employees (or a Field Frontline roster with the same layout).",
    sourceJobCodes: "From Sage PJ 6-2 Budgets (or the Accurate Metal budget template).",
    sourceJobSites: "Built from the distinct job numbers in Sage PJ 6-2 Budgets and PJ 6-3 Job Costs uploads.",
    reading: "Reading the workbook",
    added: "Added {count} {noun} from the workbook.",
    missingColumns: "That workbook doesn't have the Sage columns this page needs.",
    invalidType: "Choose an Excel workbook.",
    failed: "Could not import that workbook. Try again.",
    nounJobSites: "job sites",
    nounJobCodes: "job codes",
    nounEmployees: "employees",
  },
  adminMode: {
    title: "Admin mode",
    subtitle: "Office staff clock in from the app with these tasks.",
    tasksTitle: "Admin tasks",
    tasksHelper: "Staff pick one of these, or Custom, when they clock in. They can change task during the day.",
    addTask: "Add task",
    editTask: "Edit task",
    nameEnLabel: "Task name (English)",
    nameEsLabel: "Task name (Spanish)",
    payrollCodeLabel: "Sage cost code",
    payrollCodeHelper: "Required. A number like 9010. Admin time on this task exports to the 5-5-1 Daily Payroll under this code.",
    payrollCodeRequired: "Enter the Sage cost code as a number, like 9010.",
    sortOrderLabel: "Sort order",
    nameRequired: "Enter a task name.",
    columnTask: "Task",
    columnSpanish: "Spanish",
    columnPayrollCode: "Sage code",
    columnStatus: "Status",
    active: "Active",
    inactive: "Inactive",
    deactivate: "Deactivate",
    reactivate: "Reactivate",
    tasksEmpty: "No admin tasks yet.",
    staffTitle: "Admin mode staff",
    staffHelper: "Turn on Admin mode for office or field staff and link their app login. The app's Admin switch only works for employees turned on here.",
    columnAdminMode: "Admin mode enabled",
    adminModeOn: "On",
    adminModeOff: "Off",
    needsLogin: "Link a login so they can use it.",
    columnEmployee: "Employee",
    columnLogin: "App login",
    notLinked: "Not linked",
    staffEmpty: "No employees yet.",
    timeSheetTitle: "Admin time sheet",
    columnDate: "Date",
    columnClockIn: "Clock in",
    columnClockOut: "Clock out",
    columnTasks: "Tasks",
    columnHours: "Hours",
    columnLocation: "Sign-in location",
    columnPunches: "Clock-out / task changes",
    stillClockedIn: "Clocked in",
    autoEnded: "Auto clock-out",
    custom: "Custom",
    timeSheetEmpty: "No admin time in this date range.",
    loadFailed: "Could not load Admin mode. Try again.",
    saveFailed: "Could not save. Try again.",
  },
  timeClock: {
    title: "Time clock",
    subtitle: "Office alerts, far-away punches and time clock settings.",
    alertsTitle: "Office alerts",
    alertsHelper: "Clock-ins blocked because the phone's location was off. Allow lets that employee's next clock-in today go through without location, once.",
    alertsEmpty: "No open alerts.",
    showClosed: "Show closed",
    columnEmployee: "Employee",
    columnDate: "Date",
    columnMode: "Mode",
    columnJobSite: "Job site",
    columnAttempts: "Attempts",
    columnLastAttempt: "Last attempt",
    columnStatus: "Status",
    modeAdmin: "Admin",
    modeForeman: "Crew",
    statusOpen: "Blocked",
    statusAllowed: "Allowed by {name} at {at}",
    statusUsed: "Allowed by {name} at {at} · used",
    statusDismissed: "Dismissed by {name} at {at}",
    allow: "Allow clock-in",
    dismiss: "Dismiss",
    overrideTitle: "Allow one clock-in without location",
    overrideNoteLabel: "Note (optional)",
    overrideHelper: "{name} can clock in once today without location. Your name and the time are saved.",
    flaggedTitle: "Flagged punches",
    flaggedHelper: "Punches farther than the distance below from the job site (or the office when the job site has no coordinates). They are never blocked; review them here.",
    flaggedEmpty: "No flagged punches in this date range.",
    showReviewed: "Show reviewed",
    columnPunch: "Punch",
    columnTime: "Time",
    columnDistance: "Distance",
    columnLocation: "Location",
    columnReview: "Review",
    markReviewed: "Mark reviewed",
    reviewedBy: "Reviewed by {name} at {at}",
    kindClockIn: "Clock in",
    kindClockOut: "Clock out",
    kindTaskChange: "Task / job change",
    distanceFrom: "{miles} mi from {reference}",
    referenceJobSite: "job site",
    referenceOffice: "office",
    farFlag: "Far away",
    settingsTitle: "Time clock settings",
    settingsHelper: "Office address and coordinates are used for Admin mode punches and job sites without coordinates.",
    addressLabel: "Office address",
    cityLabel: "City",
    stateLabel: "State",
    zipLabel: "ZIP",
    latitudeLabel: "Office latitude",
    longitudeLabel: "Office longitude",
    thresholdLabel: "Far-away distance (miles)",
    thresholdHelper: "Punches farther than this are flagged for review. Default 0.5.",
    customCodeLabel: "Custom admin Sage cost code",
    customCodeHelper: "Admin time with a typed (Custom) task exports under this code.",
    adminJobLabel: "Admin Sage job number",
    adminJobHelper: "Optional. Admin time exports with this job number; blank leaves it empty.",
    saveSettings: "Save settings",
    saved: "Saved.",
    numberRequired: "{field} must be a number.",
    coordsPair: "Enter both latitude and longitude, or leave both blank.",
    unknownUser: "someone",
    loadFailed: "Could not load. Try again.",
    saveFailed: "Could not save. Try again.",
  },
  signInLocation: {
    title: "Sign-in locations",
    withAccuracy: "{coords} (±{meters} m)",
    denied: "Location permission denied",
    unavailable: "Location unavailable",
    timeout: "Location timed out",
    missing: "Not recorded",
    openMap: "Open map",
    empty: "No sign-ins on this day.",
  },
  payroll: {
    title: "Payroll export",
    helper: "Sage 100 Contractor daily payroll (PR 5-5-1 / PRDAILY). Includes crew time and Admin time (task Sage codes, or the company Custom code, with the company admin job number). Preview the rows, then download.",
    companyLabel: "Company",
    companyRequired: "Choose a company.",
    companiesLoadFailed: "Could not load companies. Try again.",
    fromLabel: "From",
    toLabel: "To",
    preview: "Preview",
    previewing: "Loading preview…",
    downloadCsv: "Download CSV",
    downloadXlsx: "Download XLSX",
    downloading: "Downloading…",
    rowCount: "{count} payroll rows",
    emptyRows: "No payroll rows in this date range.",
    warningsTitle: "Warnings",
    warningsHelper: "Sage will reject these rows until they are fixed.",
    dateRequired: "Choose a from date and a to date.",
    dateOrder: "The from date must be on or before the to date.",
    loadFailed: "Could not load the payroll export. Try again.",
    downloadFailed: "Could not download the payroll file. Try again.",
    columnPayDate: "Pay date",
    columnEmployee: "Employee",
    columnJob: "Job",
    columnCostCode: "Job code",
    columnHours: "Hours",
    columnDescription: "Description",
  },
} as const

type DeepStringify<T> = {
  [K in keyof T]: T[K] extends string ? string : DeepStringify<T[K]>
}

export type EnMessages = DeepStringify<typeof en>
